import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Divider,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import 'chart.js/auto';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import React, { useCallback, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import DidNotLoad from '../components/DidNotLoad';
import Loading from '../components/Loading';
import PremiumFeatureBadge from '../components/PremiumFeatureBadge';
import UpcomingMaintenancesPanel from '../components/printer-maintenances/UpcomingMaintenancesPanel';
import ActivePrintersPanel from '../components/printers/ActivePrintersPanel';
import PageTutorialButtons from '../components/tutorials/PageTutorialButtons';
import { useFeatureFlag } from '../helpers/featureFlags';
import { formatCurrencyInCents } from '../helpers/formatters';
import { buildPaywallUrl } from '../helpers/paywall';
import { formatFilamentGrams, formatProductionDurationMinutes } from '../helpers/jobs';
import { patchJobsSummary } from '../helpers/jobLivePatch';
import { applyJobLiveNotification } from '../helpers/jobLiveInProgress';
import { isJobIntegrationLiveNotification, subscribeLiveNotifications } from '../helpers/liveNotifications';
import { filterActivePrinters } from '../helpers/printers';
import {
    idleRowsFromActivityMap,
    loadIdlePrinterMap,
    patchActivityMapForNotification,
    resolveIdleLabel,
} from '../helpers/idlePrinters';
import { useTickingNow } from '../hooks/useTickingNow';

const cardSx = {
    height: '100%',
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: 'none',
    borderRadius: 2,
};

const sectionTitleSx = {
    fontWeight: 700,
    letterSpacing: 0.2,
    mb: 1.5,
};

function SectionTitle({ children, hint }) {
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={sectionTitleSx}>
                {children}
            </Typography>
            {hint ? (
                <Typography variant="body2" color="text.secondary">
                    {hint}
                </Typography>
            ) : null}
        </Box>
    );
}

function KpiCard({
    label,
    value,
    hint,
    details,
    onClick,
    accent = 'primary',
    valueColor,
    premiumLocked = false,
}) {
    const content = (
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    mb: 0.75,
                }}
            >
                {label}
                {premiumLocked ? <PremiumFeatureBadge sx={{ ml: 0.5 }} /> : null}
            </Typography>
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 700,
                    lineHeight: 1.15,
                    color: valueColor || 'text.primary',
                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                }}
            >
                {value}
            </Typography>
            {hint ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                    {hint}
                </Typography>
            ) : null}
            {Array.isArray(details) && details.length > 0 ? (
                <Box sx={{ mt: 1 }}>
                    {details.map((row) => (
                        <Box
                            key={row.label}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'baseline',
                                gap: 1,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                {row.label}
                            </Typography>
                            <Typography variant="caption" fontWeight={600} color="text.primary">
                                {row.value}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            ) : null}
        </CardContent>
    );

    return (
        <Card
            sx={{
                ...cardSx,
                borderLeft: '3px solid',
                borderLeftColor: `${accent}.main`,
            }}
        >
            {onClick ? (
                <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
                    {content}
                </CardActionArea>
            ) : content}
        </Card>
    );
}

function PartnerVisitsDayCard({
    title,
    count,
    visits,
    premiumLocked = false,
    onClick,
}) {
    const list = Array.isArray(visits) ? visits : [];
    const preview = list.slice(0, 3);
    const extra = list.length - preview.length;

    const content = (
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                    }}
                >
                    {title}
                    {premiumLocked ? <PremiumFeatureBadge sx={{ ml: 0.5 }} /> : null}
                </Typography>
                <Typography variant="h4" fontWeight={700} lineHeight={1} sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>
                    {premiumLocked ? '—' : (count ?? list.length)}
                </Typography>
            </Stack>
            {premiumLocked ? (
                <Typography variant="body2" color="text.secondary">
                    Recurso premium · clique para conhecer
                </Typography>
            ) : preview.length > 0 ? (
                <Stack spacing={0.75}>
                    {preview.map((visit) => (
                        <Box key={visit.uuid} sx={{ minWidth: 0 }}>
                            <Typography fontWeight={600} noWrap variant="body2">
                                {visit.partner_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                                {[visit.partner_address, visit.partner_city].filter(Boolean).join(' · ') || 'Sem endereço'}
                            </Typography>
                        </Box>
                    ))}
                    {extra > 0 ? (
                        <Typography variant="caption" color="text.secondary">
                            +{extra} mais · abrir calendário
                        </Typography>
                    ) : (
                        <Typography variant="caption" color="text.secondary">
                            Abrir calendário
                        </Typography>
                    )}
                </Stack>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    Nenhuma visita agendada.
                </Typography>
            )}
        </CardContent>
    );

    return (
        <Card sx={{ ...cardSx, borderLeft: '3px solid', borderLeftColor: 'info.main' }}>
            <CardActionArea onClick={onClick} sx={{ height: '100%', alignItems: 'stretch' }}>
                {content}
            </CardActionArea>
        </Card>
    );
}

const SALES_PAYMENT_METHODS = [
    { key: 'pix_amount', label: 'PIX', color: '#0284C7' },
    { key: 'credit_card_amount', label: 'Crédito', color: '#4F46E5' },
    { key: 'debit_card_amount', label: 'Débito', color: '#C026D3' },
    { key: 'cash_amount', label: 'Dinheiro', color: '#15803D' },
    { key: 'bank_transfer_amount', label: 'Transferência', color: '#C2410C' },
];

function parseDashboardDay(value) {
    const raw = String(value || '');
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) {
        return null;
    }
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function buildSalesRhythmSeries(rows) {
    return (Array.isArray(rows) ? rows : [])
        .map((row) => {
            const date = parseDashboardDay(row.date);
            if (!date) {
                return null;
            }
            return {
                label: format(date, 'dd/MM'),
                tooltip: format(date, "dd 'de' MMMM", { locale: ptBR }),
                amount: Number(row.amount) || 0,
            };
        })
        .filter(Boolean);
}

function SalesFunnel({ items }) {
    const theme = useTheme();
    return (
        <Paper
            variant="outlined"
            sx={{
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
            }}
        >
            <Grid container>
                {items.map((item, index) => (
                    <Grid
                        item
                        xs={12}
                        sm={items.length >= 3 ? 4 : 12 / Math.max(items.length, 1)}
                        key={item.label}
                        sx={{
                            borderRight: {
                                sm: index < items.length - 1 ? `1px solid ${theme.palette.divider}` : 0,
                            },
                            borderBottom: {
                                xs: index < items.length - 1 ? `1px solid ${theme.palette.divider}` : 0,
                                sm: 0,
                            },
                        }}
                    >
                        <Box
                            role={item.onClick ? 'button' : undefined}
                            tabIndex={item.onClick ? 0 : undefined}
                            onClick={item.onClick}
                            onKeyDown={item.onClick ? (event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    item.onClick();
                                }
                            } : undefined}
                            sx={{
                                px: { xs: 2, md: 2.5 },
                                py: { xs: 1.75, md: 2 },
                                cursor: item.onClick ? 'pointer' : 'default',
                                '&:hover': item.onClick ? { bgcolor: 'action.hover' } : undefined,
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.6,
                                    fontWeight: 700,
                                    display: 'block',
                                    mb: 0.5,
                                }}
                            >
                                {item.label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    lineHeight: 1.1,
                                    fontSize: { xs: '1.45rem', md: '1.7rem' },
                                    letterSpacing: -0.3,
                                }}
                            >
                                {item.value}
                            </Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </Paper>
    );
}

function SalesChannelRanking({ rows }) {
    const theme = useTheme();
    const total = rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);

    return (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', height: '100%', width: '100%' }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight={700}>
                    Origem
                </Typography>
            </Box>
            {rows.length === 0 ? (
                <Box sx={{ px: 2, py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Nenhuma venda com canal no período.
                    </Typography>
                </Box>
            ) : (
                <Stack divider={<Divider flexItem />}>
                    {rows.map((row, index) => {
                        const amount = Number(row.amount) || 0;
                        const share = total > 0 ? Math.round((amount / total) * 100) : 0;
                        return (
                            <Box
                                key={row.name}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    px: 2,
                                    py: 1.25,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ width: 18, flexShrink: 0, fontWeight: 700 }}
                                >
                                    {index + 1}
                                </Typography>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600} noWrap>
                                        {row.name}
                                    </Typography>
                                    <Box
                                        sx={{
                                            mt: 0.6,
                                            height: 3,
                                            borderRadius: 99,
                                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: `${share}%`,
                                                height: '100%',
                                                bgcolor: 'primary.main',
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                    <Typography variant="body2" fontWeight={700}>
                                        {formatCurrencyInCents(amount)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {share}%
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Stack>
            )}
        </Paper>
    );
}

function SalesPaymentMix({ methods, pendingCents }) {
    const theme = useTheme();
    const ranked = [...methods].sort((a, b) => b.amount - a.amount);
    const total = ranked.reduce((sum, row) => sum + row.amount, 0);
    if (ranked.length === 0 && pendingCents <= 0) {
        return null;
    }

    return (
        <Box sx={{ mt: 1.75, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            {ranked.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {ranked.map((row) => {
                        const share = total > 0 ? Math.round((row.amount / total) * 100) : 0;
                        const tone = row.color || theme.palette.primary.main;
                        return (
                            <Chip
                                key={row.label}
                                size="small"
                                variant="outlined"
                                label={(
                                    <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75 }}>
                                        <Box component="span" sx={{ fontWeight: 700 }}>
                                            {row.label}
                                            {' '}
                                            {share}%
                                        </Box>
                                        <Box component="span" sx={{ fontSize: '0.7rem', fontWeight: 500, opacity: 0.78 }}>
                                            {formatCurrencyInCents(row.amount)}
                                        </Box>
                                    </Box>
                                )}
                                sx={{
                                    height: 26,
                                    borderColor: alpha(tone, theme.palette.mode === 'dark' ? 0.45 : 0.35),
                                    bgcolor: alpha(tone, theme.palette.mode === 'dark' ? 0.16 : 0.1),
                                    color: tone,
                                    '& .MuiChip-label': {
                                        px: 1.1,
                                    },
                                }}
                            />
                        );
                    })}
                </Box>
            ) : null}
            {pendingCents > 0 ? (
                <Typography
                    variant="caption"
                    color="warning.main"
                    sx={{ display: 'block', mt: ranked.length > 0 ? 1 : 0, fontWeight: 600 }}
                >
                    {formatCurrencyInCents(pendingCents)} em aberto
                </Typography>
            ) : null}
        </Box>
    );
}

function ChartCard({ title, subtitle, footer, children, height = 300 }) {
    return (
        <Card sx={{ ...cardSx, width: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    {title}
                </Typography>
                {subtitle ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {subtitle}
                    </Typography>
                ) : null}
                <Box sx={{ height, position: 'relative', flex: '0 0 auto' }}>
                    {children}
                </Box>
                {footer || null}
            </CardContent>
        </Card>
    );
}

/** Métricas principais do período — faixa única, tipografia maior. */
function ProductionHeroMetrics({ items }) {
    const theme = useTheme();
    return (
        <Paper
            variant="outlined"
            sx={{
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: (t) => (
                    t.palette.mode === 'dark'
                        ? alpha(t.palette.primary.main, 0.08)
                        : alpha(t.palette.primary.main, 0.04)
                ),
                borderColor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.28 : 0.16),
            }}
        >
            <Grid container>
                {items.map((item, index) => (
                    <Grid
                        item
                        xs={6}
                        md={3}
                        key={item.label}
                        sx={{
                            borderRight: {
                                xs: index % 2 === 0 ? `1px solid ${theme.palette.divider}` : 0,
                                md: index < items.length - 1 ? `1px solid ${theme.palette.divider}` : 0,
                            },
                            borderBottom: {
                                xs: index < 2 ? `1px solid ${theme.palette.divider}` : 0,
                                md: 0,
                            },
                        }}
                    >
                        <Box sx={{ px: { xs: 1.75, md: 2.5 }, py: { xs: 2, md: 2.5 } }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.7,
                                    fontWeight: 700,
                                    display: 'block',
                                    mb: 0.75,
                                }}
                            >
                                {item.label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    lineHeight: 1.1,
                                    fontSize: { xs: '1.65rem', sm: '1.9rem', md: '2.05rem' },
                                    letterSpacing: -0.4,
                                }}
                            >
                                {item.value}
                            </Typography>
                            {item.hint ? (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mt: 0.75, display: 'block', lineHeight: 1.35 }}
                                >
                                    {item.hint}
                                </Typography>
                            ) : null}
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </Paper>
    );
}

/** Status do período — compacto, sem competir com o hero. */
function QuietStatusStat({ label, value, hint, onClick }) {
    return (
        <Box
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick}
            onKeyDown={onClick ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onClick();
                }
            } : undefined}
            sx={{
                height: '100%',
                minHeight: 108,
                px: 1.75,
                py: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'background-color 0.15s ease',
                '&:hover': onClick ? { bgcolor: 'action.hover' } : undefined,
            }}
        >
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, letterSpacing: 0.4, display: 'block', mb: 0.35 }}
            >
                {label}
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {value}
            </Typography>
            {hint ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                    {hint}
                </Typography>
            ) : null}
        </Box>
    );
}

function IdlePrintersHighlight({ rows, onClick }) {
    const theme = useTheme();
    const now = useTickingNow(15000);
    const list = Array.isArray(rows) ? rows : [];
    const hasIdle = list.length > 0;
    const tone = hasIdle ? theme.palette.error.main : theme.palette.success.main;

    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onClick?.();
                }
            }}
            sx={{
                height: '100%',
                minHeight: 108,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: alpha(tone, hasIdle ? 0.45 : 0.28),
                bgcolor: alpha(tone, hasIdle ? 0.1 : 0.06),
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'stretch' },
                gap: 1.5,
                transition: 'background-color 0.15s ease',
                '&:hover': { bgcolor: alpha(tone, hasIdle ? 0.14 : 0.1) },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: { sm: 120 },
                    flexShrink: 0,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        textTransform: 'uppercase',
                        color: tone,
                        mb: 0.35,
                    }}
                >
                    Impressoras ociosas
                </Typography>
                <Typography
                    sx={{
                        fontWeight: 800,
                        fontSize: { xs: '1.75rem', md: '2rem' },
                        lineHeight: 1.1,
                        color: tone,
                        letterSpacing: -0.5,
                    }}
                >
                    {rows == null ? '—' : list.length}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.35 }}>
                    {hasIdle
                        ? 'Paradas há mais de 10 min'
                        : 'Nenhuma ociosa no momento'}
                </Typography>
            </Box>
            {hasIdle ? (
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        borderLeft: { sm: '1px solid' },
                        borderColor: { sm: alpha(tone, 0.25) },
                        pl: { sm: 1.5 },
                        maxHeight: 120,
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                    }}
                >
                    {list.map((row) => (
                        <Box
                            key={row.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                justifyContent: 'space-between',
                                gap: 1,
                                minWidth: 0,
                            }}
                        >
                            <Typography
                                variant="body2"
                                fontWeight={600}
                                noWrap
                                sx={{ color: 'text.primary', minWidth: 0 }}
                            >
                                {row.name}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 700,
                                    color: tone,
                                    flexShrink: 0,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {(() => {
                                    const liveLabel = row.lastActivityAt != null
                                        ? resolveIdleLabel(row.lastActivityAt, now)
                                        : (row.idleLabel || 'sem jobs');
                                    return liveLabel === 'sem jobs'
                                        ? 'sem jobs'
                                        : `parada há ${liveLabel}`;
                                })()}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            ) : null}
        </Box>
    );
}

/** Equipamentos e estoque em uma lista única. */
function ProductionAssetsList({ rows }) {
    return (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight={700}>
                    Equipamentos e estoque
                </Typography>
            </Box>
            <Stack divider={<Divider flexItem />}>
                {rows.map((row) => (
                    <Box
                        key={row.label}
                        role="button"
                        tabIndex={0}
                        onClick={row.onClick}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                row.onClick?.();
                            }
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 2,
                            py: 1.35,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600}>
                                {row.label}
                            </Typography>
                            {row.hint ? (
                                <Typography variant="caption" color="text.secondary">
                                    {row.hint}
                                </Typography>
                            ) : null}
                        </Box>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ flexShrink: 0 }}>
                            {row.value}
                        </Typography>
                        <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled', flexShrink: 0 }} />
                    </Box>
                ))}
            </Stack>
        </Paper>
    );
}

function Dashboard() {
    const initialData = {
        printers_totals: {
            total: 0,
            total_amount: 0,
        },
        materials_total: {
            total: 0,
            total_amount: 0,
        },
        products_total: 0,
        checkouts_total: 0,
        orders_total: 0,
        quotes_total: 0,
        orders_amount: {
            total_amount: 0,
            cash_amount: 0,
            pix_amount: 0,
            credit_card_amount: 0,
            debit_card_amount: 0,
            bank_transfer_amount: 0,
            barter_amount: 0,
            pending_payment: 0,
        },
        checkouts_amount: 0,
        sales_by_channel_amount: {
            channels: [],
        },
        sales_by_day_amount: {
            sales: [],
        },
        customers_total: 0,
        transactions_summary: {
            total_income: 0,
            total_outcome: 0,
            net_amount: 0,
            total_transactions: 0,
        },
        operational_costs_month_total: 0,
        replacement_parts_total: {
            total: 0,
            total_amount: 0,
        },
        maintenances_total: 0,
        jobs_summary: {
            total: 0,
            total_objects: 0,
            in_progress: 0,
            completed: 0,
            issues: 0,
            total_duration_minutes: 0,
            total_filament_grams: 0,
            total_printed_objects: 0,
            success_rate_objects: 0,
            top_printer: null,
        },
    };
    const [data, setData] = useState(initialData);
    const [idleActivityByPrinter, setIdleActivityByPrinter] = useState(null);
    const idleNow = useTickingNow(15000);
    const idlePrinterRows = idleActivityByPrinter == null
        ? null
        : idleRowsFromActivityMap(idleActivityByPrinter, idleNow);

    const [value, setValue] = useState(() => {
        const today = new Date();
        return [startOfMonth(today), endOfMonth(today)];
    });
    const [isLoading, setIsLoading] = useState(true);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const isLightTheme = theme.palette.mode === 'light';
    const { enabled: hasFinanceAccess, loading: financeFlagLoading } = useFeatureFlag('finance');
    const financeLocked = !financeFlagLoading && !hasFinanceAccess;
    const { enabled: hasQuotesAccess, loading: quotesFlagLoading } = useFeatureFlag('quotes');
    const quotesLocked = !quotesFlagLoading && !hasQuotesAccess;
    const { enabled: hasOperationalCostsAccess, loading: operationalCostsFlagLoading } = useFeatureFlag('operational_costs');
    const operationalCostsLocked = !operationalCostsFlagLoading && !hasOperationalCostsAccess;
    const { enabled: hasPartnersAccess, loading: partnersFlagLoading } = useFeatureFlag('commerce_partners');
    const partnersLocked = !partnersFlagLoading && !hasPartnersAccess;
    const [partnersVisits, setPartnersVisits] = useState(null);

    const refreshIdlePrinters = useCallback(() => (
        Promise.all([
            api.get('printers?page=1&limit=200'),
            api.get('jobs?status=em_andamento&limit=200'),
        ])
            .then(async ([printersRes, inProgressRes]) => {
                const printers = filterActivePrinters(printersRes.data.printers || []);
                const inProgressJobs = inProgressRes.data.data?.jobs || [];
                const { activityMap } = await loadIdlePrinterMap(api, printers, inProgressJobs);
                setIdleActivityByPrinter(activityMap);
            })
            .catch(() => {
                setIdleActivityByPrinter(null);
            })
    ), []);

    const handleFaturamentoClick = () => {
        if (financeLocked) {
            navigate(buildPaywallUrl({ returnTo: '/admin/transactions', featureKey: 'finance' }));
            return;
        }
        navigate('/admin/transactions');
    };

    const handleQuotesClick = () => {
        if (quotesLocked) {
            navigate(buildPaywallUrl({ returnTo: '/admin/quotes', featureKey: 'quotes' }));
            return;
        }
        navigate('/admin/quotes');
    };

    const handleOperationalCostsClick = () => {
        if (operationalCostsLocked) {
            navigate(buildPaywallUrl({ returnTo: '/admin/operational-costs', featureKey: 'operational_costs' }));
            return;
        }
        navigate('/admin/operational-costs');
    };

    const handlePartnersVisitsClick = () => {
        if (partnersLocked) {
            navigate(buildPaywallUrl({ returnTo: '/admin/partners/calendar', featureKey: 'commerce_partners' }));
            return;
        }
        navigate('/admin/partners/calendar');
    };

    const periodLabel = value[0] && value[1]
        ? `${value[0].toLocaleDateString('pt-BR')} – ${value[1].toLocaleDateString('pt-BR')}`
        : null;

    const fetchData = (startDate, endDate) => {
        setIsLoading(true);
        const start = startDate ? format(startDate, 'yyyy-MM-dd') : '';
        const end = endDate ? format(endDate, 'yyyy-MM-dd') : '';

        const loadPromise = api.get('dashboard/', {
            params: start && end ? { start_date: start, end_date: end } : {},
        }).then((response) => {
            const payload = response.data.data ?? response.data;
            if (typeof payload === 'undefined') {
                throw new Error('Resposta sem dados do dashboard.');
            }
            return payload;
        });

        loadPromise
            .then((payload) => {
                setData(payload);
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        fetchData(value[0], value[1]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        refreshIdlePrinters();
    }, [refreshIdlePrinters]);

    useEffect(() => {
        if (partnersFlagLoading) {
            return undefined;
        }
        if (!hasPartnersAccess) {
            setPartnersVisits(null);
            return undefined;
        }
        let cancelled = false;
        api.get('/commerce-partners/dashboard')
            .then((response) => {
                if (cancelled) return;
                setPartnersVisits(response?.data?.data || null);
            })
            .catch(() => {
                if (cancelled) return;
                setPartnersVisits(null);
            });
        return () => {
            cancelled = true;
        };
    }, [hasPartnersAccess, partnersFlagLoading]);

    useEffect(() => {
        return subscribeLiveNotifications((detail) => {
            if (!isJobIntegrationLiveNotification(detail)) {
                return;
            }
            applyJobLiveNotification(detail);
            setData((prev) => ({
                ...prev,
                jobs_summary: patchJobsSummary(prev.jobs_summary, detail),
            }));
            if (detail.action === 'started' || detail.action === 'finished') {
                setIdleActivityByPrinter((prev) => {
                    if (!(prev instanceof Map)) {
                        return prev;
                    }
                    return patchActivityMapForNotification(prev, detail);
                });
            }
        });
    }, []);

    const handleApplyFilter = () => {
        fetchData(value[0], value[1]);
    };

    const handleResetToCurrentMonth = () => {
        const today = new Date();
        const next = [startOfMonth(today), endOfMonth(today)];
        setValue(next);
        fetchData(next[0], next[1]);
    };

    const salesChannelRows = Array.isArray(data.sales_by_channel_amount?.channels)
        ? data.sales_by_channel_amount.channels
        : [];
    const salesRhythmRows = buildSalesRhythmSeries(data.sales_by_day_amount?.sales);
    const salesByDayData = {
        labels: salesRhythmRows.map((row) => row.label),
        datasets: [
            {
                label: 'Vendas',
                data: salesRhythmRows.map((row) => row.amount / 100),
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                fill: true,
                tension: 0.25,
                pointRadius: 3,
                pointHoverRadius: 6,
                pointBackgroundColor: theme.palette.primary.main,
                pointBorderColor: theme.palette.background.paper,
                pointBorderWidth: 2,
                pointHitRadius: 12,
                borderWidth: 2,
            },
        ],
    };
    const paymentMix = SALES_PAYMENT_METHODS
        .map((method) => ({
            label: method.label,
            color: method.color,
            amount: Number(data.orders_amount[method.key]) || 0,
        }))
        .filter((row) => row.amount > 0)
        .sort((a, b) => b.amount - a.amount);
    const pendingPaymentCents = Number(data.orders_amount.pending_payment) || 0;

    const moneyBarOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'nearest',
            intersect: false,
            axis: 'x',
        },
        scales: {
            x: {
                ticks: {
                    maxRotation: 0,
                    minRotation: 0,
                    autoSkip: true,
                    maxTicksLimit: 8,
                    color: theme.palette.text.secondary,
                    font: { size: 11 },
                },
                grid: { display: false },
                border: { display: false },
            },
            y: {
                beginAtZero: true,
                ticks: {
                    color: theme.palette.text.secondary,
                    font: { size: 11 },
                    callback(tickValue) {
                        return `R$ ${Number(tickValue).toFixed(0)}`;
                    },
                },
                grid: {
                    color: alpha(theme.palette.divider, 0.7),
                    drawBorder: false,
                },
                border: { display: false },
            },
        },
        plugins: {
            tooltip: {
                callbacks: {
                    title(items) {
                        const index = items[0]?.dataIndex;
                        return salesRhythmRows[index]?.tooltip || items[0]?.label || '';
                    },
                    label(context) {
                        return `R$ ${Number(context.parsed.y).toFixed(2).replace('.', ',')}`;
                    },
                },
            },
            legend: {
                display: false,
            },
        },
    };

    const jobsTotalInPeriod = data.jobs_summary?.total ?? (
        (data.jobs_summary?.in_progress ?? 0)
        + (data.jobs_summary?.completed ?? 0)
        + (data.jobs_summary?.issues ?? 0)
    );
    const jobsObjectsInPeriod = data.jobs_summary?.total_objects ?? 0;
    const jobsSuccessRate = Number(data.jobs_summary?.success_rate_objects) || 0;
    const jobsDurationMinutes = data.jobs_summary?.total_duration_minutes ?? 0;
    const jobsFilamentGrams = data.jobs_summary?.total_filament_grams ?? 0;
    const topPrinter = data.jobs_summary?.top_printer;
    const totalSalesCents = Number(data.orders_amount.total_amount || 0);
    const incomeCents = Number(data.transactions_summary.total_income || 0);
    const outcomeCents = Number(data.transactions_summary.total_outcome || 0);
    const monthCostsCents = Number(data.operational_costs_month_total || 0);
    const expensesCents = outcomeCents + monthCostsCents;
    const balanceCents = incomeCents - expensesCents;

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 1, sm: 2 }, pb: 4 }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 1.5,
                    mb: 2.5,
                }}
            >
                <Box>
                    <Typography variant="h4" fontWeight={700}>
                        Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Visão geral do período selecionado
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
                    {periodLabel ? (
                        <Chip
                            label={periodLabel}
                            color="primary"
                            variant="outlined"
                            size="small"
                        />
                    ) : null}
                    {isLightTheme ? (
                        <PageTutorialButtons
                            pageKey="dashboard"
                            buttonLabel="prefere o tema escuro?"
                            variant="link"
                        />
                    ) : null}
                </Box>
            </Box>

            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                }}
            >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                    Período
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1.5,
                            alignItems: 'center',
                        }}
                    >
                        <DatePicker
                            label="Data inicial"
                            value={value[0]}
                            onChange={(newValue) => setValue([newValue, value[1]])}
                            renderInput={(params) => (
                                <TextField {...params} size="small" sx={{ minWidth: 160 }} />
                            )}
                        />
                        <DatePicker
                            label="Data final"
                            value={value[1]}
                            onChange={(newValue) => setValue([value[0], newValue])}
                            renderInput={(params) => (
                                <TextField {...params} size="small" sx={{ minWidth: 160 }} />
                            )}
                        />
                        <Button variant="contained" onClick={handleApplyFilter}>
                            Aplicar filtro
                        </Button>
                        <Button variant="outlined" onClick={handleResetToCurrentMonth}>
                            Mês atual
                        </Button>
                    </Box>
                </LocalizationProvider>
            </Paper>

            <SectionTitle hint="Clique no faturamento para abrir o financeiro">
                Financeiro
            </SectionTitle>
            <Grid container spacing={2} sx={{ mb: 3.5 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        label="Faturamento"
                        value={formatCurrencyInCents(totalSalesCents)}
                        hint={
                            financeLocked
                                ? 'Recurso premium'
                                : 'Total de vendas do período'
                        }
                        accent="success"
                        valueColor="success.main"
                        onClick={handleFaturamentoClick}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        label="Recebimentos líquidos"
                        value={formatCurrencyInCents(incomeCents)}
                        hint="Valor líquido das transações de entrada"
                        accent="success"
                        valueColor="success.main"
                        onClick={handleFaturamentoClick}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        label="Despesas"
                        value={formatCurrencyInCents(expensesCents)}
                        details={[
                            {
                                label: 'Custo do mês',
                                value: formatCurrencyInCents(monthCostsCents),
                            },
                            {
                                label: 'Saídas',
                                value: formatCurrencyInCents(outcomeCents),
                            },
                        ]}
                        accent="warning"
                        valueColor="warning.main"
                        premiumLocked={operationalCostsLocked}
                        onClick={handleOperationalCostsClick}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <KpiCard
                        label="Saldo"
                        value={formatCurrencyInCents(balanceCents)}
                        hint="Recebimentos líquidos − despesas"
                        accent={balanceCents < 0 ? 'error' : 'info'}
                        valueColor={balanceCents < 0 ? 'error.main' : 'info.main'}
                        onClick={handleFaturamentoClick}
                    />
                </Grid>
                {financeLocked ? (
                    <Grid item xs={12}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                Financeiro completo
                            </Typography>
                            <PremiumFeatureBadge />
                        </Box>
                    </Grid>
                ) : null}
            </Grid>

            <SectionTitle>
                Pedidos
            </SectionTitle>
            <SalesFunnel
                items={[
                    {
                        label: 'Orçamentos',
                        value: data.quotes_total,
                        onClick: handleQuotesClick,
                    },
                    {
                        label: 'Pedidos',
                        value: data.orders_total,
                        onClick: () => navigate('/admin/orders'),
                    },
                ]}
            />
            <Grid container spacing={2} sx={{ mb: 4 }} alignItems="stretch">
                <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
                    <ChartCard
                        title="Ritmo"
                        height={280}
                        footer={(
                            <SalesPaymentMix
                                methods={paymentMix}
                                pendingCents={pendingPaymentCents}
                            />
                        )}
                    >
                        {salesRhythmRows.length > 0 ? (
                            <Line data={salesByDayData} options={moneyBarOptions} />
                        ) : (
                            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Sem vendas no período.
                                </Typography>
                            </Box>
                        )}
                    </ChartCard>
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                    <SalesChannelRanking rows={salesChannelRows} />
                </Grid>
            </Grid>

            <SectionTitle
                hint={`${jobsTotalInPeriod} impressões · ${jobsObjectsInPeriod} objetos no período`}
            >
                Produção
            </SectionTitle>

            <ProductionHeroMetrics
                items={[
                    {
                        label: 'Tempo de impressão',
                        value: formatProductionDurationMinutes(jobsDurationMinutes),
                        hint: 'Estimado no período',
                    },
                    {
                        label: 'Filamento',
                        value: formatFilamentGrams(jobsFilamentGrams),
                        hint: 'Estimado no período',
                    },
                    {
                        label: 'Sucesso em objetos',
                        value: `${jobsSuccessRate.toFixed(1).replace('.', ',')}%`,
                        hint: 'Objetos concluídos sem falha',
                    },
                    {
                        label: 'Impressora mais usada',
                        value: topPrinter?.name || '—',
                        hint: topPrinter?.name
                            ? `${formatProductionDurationMinutes(topPrinter.total_duration_minutes)} · ${topPrinter.duration_share_percent?.toFixed?.(0) ?? topPrinter.duration_share_percent}% · ${topPrinter.total_jobs} jobs`
                            : 'Sem dados no período',
                    },
                ]}
            />

            <Grid container spacing={1.5} sx={{ mb: 2 }} alignItems="stretch">
                <Grid item xs={6} md={2}>
                    <QuietStatusStat
                        label="Em andamento"
                        value={data.jobs_summary?.in_progress ?? 0}
                        hint="Agora"
                        onClick={() => navigate('/admin/jobs')}
                    />
                </Grid>
                <Grid item xs={6} md={2}>
                    <QuietStatusStat
                        label="Concluídas"
                        value={data.jobs_summary?.completed ?? 0}
                        hint="No período"
                        onClick={() => navigate('/admin/jobs')}
                    />
                </Grid>
                <Grid item xs={6} md={2}>
                    <QuietStatusStat
                        label="Com erro"
                        value={data.jobs_summary?.issues ?? 0}
                        hint="Falha / parcial"
                        onClick={() => navigate('/admin/jobs')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <IdlePrintersHighlight
                        rows={idlePrinterRows}
                        onClick={() => navigate('/admin/printers')}
                    />
                </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
                <ActivePrintersPanel />
            </Box>

            <ProductionAssetsList
                rows={[
                    {
                        label: 'Impressoras',
                        value: data.printers_totals.total,
                        hint: formatCurrencyInCents(data.printers_totals.total_amount),
                        onClick: () => navigate('/admin/printers'),
                    },
                    {
                        label: 'Filamentos',
                        value: data.materials_total.total,
                        hint: formatCurrencyInCents(data.materials_total.total_amount),
                        onClick: () => navigate('/admin/materials'),
                    },
                    {
                        label: 'Peças de reposição',
                        value: data.replacement_parts_total?.total ?? 0,
                        hint: formatCurrencyInCents(data.replacement_parts_total?.total_amount ?? 0),
                        onClick: () => navigate('/admin/replacement-parts'),
                    },
                    {
                        label: 'Manutenções',
                        value: data.maintenances_total ?? 0,
                        hint: 'Registros no histórico',
                        onClick: () => navigate('/admin/printer-maintenances'),
                    },
                ]}
            />

            <Box sx={{ mb: 3.5 }}>
                <UpcomingMaintenancesPanel compact />
            </Box>

            <Divider sx={{ mb: 3 }} />

            <SectionTitle hint="Clique para abrir o calendário de visitas">
                Parcerias
            </SectionTitle>
            <Grid container spacing={2} sx={{ mb: 3.5 }}>
                <Grid item xs={12} md={6}>
                    <PartnerVisitsDayCard
                        title="Visitas hoje"
                        count={partnersVisits?.today_count}
                        visits={partnersVisits?.today_visits}
                        premiumLocked={partnersLocked}
                        onClick={handlePartnersVisitsClick}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <PartnerVisitsDayCard
                        title="Visitas amanhã"
                        count={partnersVisits?.tomorrow_count}
                        visits={partnersVisits?.tomorrow_visits}
                        premiumLocked={partnersLocked}
                        onClick={handlePartnersVisitsClick}
                    />
                </Grid>
            </Grid>

            <SectionTitle hint="Base comercial (totais do cadastro)">
                Comercial
            </SectionTitle>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <KpiCard
                        label="Clientes"
                        value={data.customers_total}
                        accent="primary"
                        onClick={() => navigate('/admin/customers')}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <KpiCard
                        label="Produtos"
                        value={data.products_total}
                        accent="primary"
                        onClick={() => navigate('/admin/products')}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}

export default Dashboard;
